import { useEffect, useMemo, useRef, useState } from "react";
import {
  type EarningsParams,
  type EarningsSeries,
  fetchEarningsSeries,
} from "../api";
import { formatEur } from "../format";
import { PeriodPicker } from "./PeriodPicker";

/**
 * Earnings-over-time widget on the Prijzen tab: PeriodPicker range,
 * big net-total tile, per-bucket bars (accent = earned, price-high =
 * cost) with the shared scrub idiom. Custom ranges hit
 * `/api/earnings/series?range=custom&start=&end=`; the backend picks
 * the bucket from the duration. A sequence counter drops stale
 * responses so a slow "Vorig jaar" cannot clobber a fresh "Vandaag".
 * docs/customer-app.md > Front-end > Period picker
 */

export function EarningsHistory() {
  const [params, setParams] = useState<EarningsParams>({ range: "today" });
  const [data, setData] = useState<EarningsSeries | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  // Stringify the params for the effect dep - react treats object
  // identity not deep-equality, so an inline {range:'today'} would
  // re-fire every render. Stable key = stable effect.
  const paramsKey = useMemo(
    () => JSON.stringify(params),
    [params],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const my = ++seq.current;
    fetchEarningsSeries(params)
      .then((v) => {
        if (!cancelled && my === seq.current) {
          setData(v);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled && my === seq.current) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled && my === seq.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const totalEarned = data?.eur_earned.reduce((a, b) => a + b, 0) ?? 0;
  const totalSpent  = data?.eur_spent.reduce((a, b) => a + b, 0) ?? 0;
  const totalNet    = totalEarned - totalSpent;

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-2">
        <h2 className="text-xs uppercase tracking-wider text-ink-mute">
          Saldo
        </h2>
        <PeriodPicker value={params} onChange={setParams} />
      </header>

      <div className="card p-4 flex flex-col gap-4">
        {error ? (
          <div className="text-sm text-danger">{error}</div>
        ) : data == null ? (
          <div className="h-32 grid place-items-center text-ink-mute">laden…</div>
        ) : data.contract == null ? (
          <div className="text-sm text-ink-mute">
            Geen energiecontract geconfigureerd. Stel het in via Account.
          </div>
        ) : (
          <>
            <div className={loading ? "opacity-60" : ""}>
              {/* Magnitude-first headline - same reasoning as
                  HomeEarnings: "€ -2,69" reads weird; "€ 2,69 kosten"
                  reads as the bill. Color carries direction (green
                  = winst, ink = kosten - never red, this is a normal
                  market signal not a fault). */}
              <div
                className={`text-3xl font-semibold ${
                  totalNet >= 0 ? "text-accent" : "text-ink"
                }`}
              >
                {formatEur(Math.abs(totalNet))}
              </div>
              <div className="text-xs text-ink-mute mt-1">
                {totalNet >= 0 ? "winst" : "kosten"}
                {" · "}
                {data.contract === "dynamic" ? "dynamisch" : "vast"} tarief
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Mini label="Verdiend" value={totalEarned} hint="teruggeleverd" />
                <Mini label="Uitgegeven" value={totalSpent} hint="afgenomen" />
              </div>
            </div>
            {data.t.length === 0 ? (
              <div className="text-xs text-ink-mute">
                Nog geen data voor deze periode.
              </div>
            ) : (
              <BucketBars data={data} />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function Mini({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint: string;
}) {
  return (
    <div>
      <div className="text-xs text-ink-mute">{label}</div>
      <div className="text-lg font-medium mt-0.5">{formatEur(value)}</div>
      <div className="text-xs text-ink-mute">{hint}</div>
    </div>
  );
}

/* ────────────── per-bucket bar chart ────────────── */

const VB_W = 600;
const PAD = { left: 8, right: 8, top: 22, bottom: 22 };

const COLOR_POS  = "var(--color-accent,     #10b981)";   // net earning
const COLOR_NEG  = "var(--color-price-high, #ea580c)";   // net cost
const COLOR_ZERO = "var(--color-line,       rgba(0,0,0,0.08))";

function BucketBars({ data }: { data: EarningsSeries }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibratedIdx = useRef<number | null>(null);

  const height = 140;
  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = height - PAD.top - PAD.bottom;
  const n = data.t.length;
  const barW = innerW / n;

  // y range - make sure 0 is always visible so positive/negative
  // semantics are obvious.
  const maxNet = Math.max(0, ...data.eur_net);
  const minNet = Math.min(0, ...data.eur_net);
  const range = (maxNet - minNet) || 1;
  const yMax = maxNet + range * 0.1;
  const yMin = minNet - (minNet < 0 ? range * 0.05 : 0);
  const yToPx = (y: number) => PAD.top + innerH * (1 - (y - yMin) / (yMax - yMin));
  const y0 = yToPx(0);

  function pointerToIndex(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    const x = Math.max(PAD.left, Math.min(VB_W - PAD.right, vbX));
    const frac = (x - PAD.left) / innerW;
    return Math.min(n - 1, Math.max(0, Math.floor(frac * n)));
  }
  function setFromClient(clientX: number, isTouch: boolean) {
    const idx = pointerToIndex(clientX);
    if (idx === null) return;
    setHoverIdx(idx);
    if (isTouch && idx !== lastVibratedIdx.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibratedIdx.current = idx;
    }
  }
  function clearScrub() {
    setHoverIdx(null);
    lastVibratedIdx.current = null;
  }

  const focusIdx = hoverIdx;
  const focused = focusIdx != null ? {
    t: data.t[focusIdx],
    net: data.eur_net[focusIdx],
    earned: data.eur_earned[focusIdx],
    spent: data.eur_spent[focusIdx],
  } : null;

  return (
    <div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${height}`}
        className="w-full select-none"
        style={{ touchAction: "pan-y" }}
        data-swipe-consume="true"
        onMouseMove={(e) => setFromClient(e.clientX, false)}
        onMouseLeave={clearScrub}
        onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
        onTouchEnd={clearScrub}
        onTouchCancel={clearScrub}
      >
        {/* Faint 0-line - anchor for negative bars when they exist. */}
        <line
          x1={PAD.left}
          x2={VB_W - PAD.right}
          y1={y0}
          y2={y0}
          stroke={COLOR_ZERO}
          strokeWidth={1}
        />

        {data.t.map((ts, i) => {
          const v = data.eur_net[i];
          const x = PAD.left + i * barW + barW * 0.12;
          const w = barW * 0.76;
          const y = v >= 0 ? yToPx(v) : y0;
          const barH = Math.abs(yToPx(v) - y0);
          const fill = v >= 0 ? COLOR_POS : COLOR_NEG;
          const isHover = i === hoverIdx;
          return (
            <rect
              key={ts}
              x={x}
              y={y}
              width={w}
              height={Math.max(barH, 1.2)}
              fill={fill}
              rx={2}
              opacity={isHover ? 1 : 0.85}
            />
          );
        })}

        {/* X-axis labels - only at periodic indices to avoid clutter. */}
        {data.t.map((ts, i) => {
          const label = axisLabelFor(ts, data.bucket);
          if (!label) return null;
          // Show at most ~6 labels regardless of n.
          const step = Math.max(1, Math.floor(n / 6));
          if (i % step !== 0) return null;
          return (
            <text
              key={`l${ts}`}
              x={PAD.left + i * barW + barW / 2}
              y={height - 6}
              textAnchor="middle"
              fontSize={10}
              className="fill-ink-mute"
            >
              {label}
            </text>
          );
        })}
      </svg>

      {focused && (
        <div className="text-xs text-ink-mute mt-1 px-1 flex justify-between gap-2">
          <span>{formatBucketTs(focused.t, data.bucket)}</span>
          <span>
            +{formatEur(focused.earned)} · −{formatEur(focused.spent)} ·{" "}
            <span className={focused.net >= 0 ? "text-accent" : "text-ink"}>
              {formatEur(focused.net)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

/* ────────────── axis labels ────────────── */

function axisLabelFor(unixSec: number, bucket: string): string | null {
  const d = new Date(unixSec * 1000);
  switch (bucket) {
    case "hour":
      return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    case "day":
      return d.toLocaleDateString("nl-NL", { day: "numeric" });
    case "month":
      return d.toLocaleDateString("nl-NL", { month: "short" });
    default:
      return null;
  }
}

function formatBucketTs(unixSec: number, bucket: string): string {
  const d = new Date(unixSec * 1000);
  switch (bucket) {
    case "hour":
      return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
    case "day":
      return d.toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
      });
    case "month":
      return d.toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
    default:
      return new Date(unixSec * 1000).toISOString();
  }
}
